import type { OfflineDb } from './db'
import { decryptBytes, deriveWrapKey, encryptBytes, randomSalt } from './crypto'
import type { OfflineSigningRecord } from './types'

/**
 * Offline Part 11 signing (FF-AUDIT-089, owner decision 2026-09-13 — option C).
 *
 * The server issues an HMAC signing key ONLINE, after the same re-authentication
 * a live signature demands. This module keeps that key on the device wrapped
 * under a key derived from the signer's PASSWORD — not the PIN — so typing the
 * password offline is the re-authentication: a wrong password cannot unwrap
 * the key (AES-GCM fails authentication) and therefore cannot produce a proof.
 * The plaintext key never touches storage. The record sits inside the vault
 * entry, which requires offline security (PIN) to have been enabled first.
 */

const ITERATIONS = 210_000

export interface OfflineSigningStatus {
  credentialId: string
  expiresAt: string
  reauthMethod: string
  storedAt: string
}

function subtle(): SubtleCrypto {
  const c = globalThis.crypto
  if (!c?.subtle) throw new Error('WebCrypto is unavailable in this environment.')
  return c.subtle
}

function bytesFromBase64(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

function hex(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Seal a freshly issued credential under the signer's password. Requires the vault entry (offline security) to exist. */
export async function storeOfflineSigning(
  db: OfflineDb,
  userId: string,
  input: { credentialId: string; signingKey: string; expiresAt: string; reauthMethod: string },
  password: string,
): Promise<OfflineSigningStatus> {
  const record = await db.vault.get(userId)
  if (!record) throw new Error('Enable offline security on this device before enabling offline signing.')
  const salt = randomSalt()
  const wrapKey = await deriveWrapKey(password, salt, ITERATIONS)
  const wrappedKey = await encryptBytes(bytesFromBase64(input.signingKey), wrapKey)
  const signing: OfflineSigningRecord = {
    credentialId: input.credentialId,
    salt,
    iterations: ITERATIONS,
    wrappedKey,
    expiresAt: input.expiresAt,
    reauthMethod: input.reauthMethod,
    storedAt: new Date().toISOString(),
  }
  await db.vault.update(userId, { signing })
  return { credentialId: signing.credentialId, expiresAt: signing.expiresAt, reauthMethod: signing.reauthMethod, storedAt: signing.storedAt }
}

/** What this device holds for the user — undefined when offline signing is not enabled here or has expired. */
export async function offlineSigningStatus(db: OfflineDb, userId: string): Promise<OfflineSigningStatus | undefined> {
  const record = await db.vault.get(userId)
  const signing = record?.signing
  if (!signing) return undefined
  if (new Date(signing.expiresAt).getTime() <= Date.now()) return undefined
  return { credentialId: signing.credentialId, expiresAt: signing.expiresAt, reauthMethod: signing.reauthMethod, storedAt: signing.storedAt }
}

export async function clearOfflineSigning(db: OfflineDb, userId: string): Promise<void> {
  const record = await db.vault.get(userId)
  if (!record?.signing) return
  await db.vault.update(userId, { signing: undefined })
}

export interface OfflineSigner {
  credentialId: string
  reauthMethod: string
  /** HMAC-SHA256 (lowercase hex) over the payload under the credential's key. */
  sign(payload: string): Promise<string>
}

/** Re-authenticate offline: the password must unwrap the sealed key. Throws on a wrong password. */
export async function unlockOfflineSigning(db: OfflineDb, userId: string, password: string): Promise<OfflineSigner> {
  const record = await db.vault.get(userId)
  const signing = record?.signing
  if (!signing) throw new Error('Offline signing is not enabled on this device.')
  if (new Date(signing.expiresAt).getTime() <= Date.now()) throw new Error('The offline signing credential on this device has expired — reconnect to renew it.')
  const wrapKey = await deriveWrapKey(password, signing.salt, signing.iterations)
  let keyBytes: ArrayBuffer
  try {
    keyBytes = await decryptBytes(signing.wrappedKey, wrapKey)
  } catch {
    throw new Error('Password not accepted.')
  }
  const key = await subtle().importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  return {
    credentialId: signing.credentialId,
    reauthMethod: signing.reauthMethod,
    sign: async (payload: string) => hex(await subtle().sign('HMAC', key, new TextEncoder().encode(payload))),
  }
}
